// La cartera de crédito de los clientes que la tabla ya trajo: SCI, nivel, créditos, saldo y mora.
// Se lee por LOTE: los conteos de la barra hablan de la cartera completa, no de la página que se ve.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    EMPTY_PORTFOLIO,
    portfolioSignatureOf,
    portfolioStateOf,
    type CreditPortfolioMap,
    type CreditPortfolioState,
} from '../lib/creditPortfolio';
import { DEFAULT_PORTFOLIO_DEPS, loadCreditPortfolio } from './creditPortfolioLoader';

export interface CreditPortfolioResult {
    byCustomer: CreditPortfolioMap;
    state: CreditPortfolioState;
    refetch: () => void;
}

interface PortfolioStateInput {
    /** La lista de clientes de la que salen los ids todavía está en vuelo. */
    isSourceLoading: boolean;
    idCount: number;
    portfolio: CreditPortfolioMap | undefined;
    error: unknown;
}

/**
 * La cartera de crédito de una lista de clientes. `isSourceLoading` dice si la lista de la que salen
 * los ids todavía se está trayendo, porque vacía-porque-carga no es vacía-porque-no-hay.
 */
export function useCreditPortfolio(
    retailCustomerIds: string[],
    isSourceLoading: boolean,
): CreditPortfolioResult {
    const ids = useMemo(() => [...retailCustomerIds].sort(), [retailCustomerIds]);
    const signature = useMemo(() => portfolioSignatureOf(ids), [ids]);

    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- la firma resume `ids`
    const query = useQuery({
        queryKey: ['loan-credit', 'portfolio', signature],
        queryFn: () => loadCreditPortfolio(ids, DEFAULT_PORTFOLIO_DEPS),
        enabled: ids.length > 0,
        staleTime: 60_000,
    });

    return {
        byCustomer: query.data ?? EMPTY_PORTFOLIO,
        state: stateOf({
            isSourceLoading,
            idCount: ids.length,
            portfolio: query.data,
            error: query.error,
        }),
        refetch: () => {
            void query.refetch();
        },
    };
}

/**
 * Mientras la lista de clientes viaja no hay cartera que juzgar: `loading`. Recién cuando la lista
 * llegó y trajo cero clientes la cartera está leída, y una cartera vacía no tiene celdas sin leer.
 */
function stateOf(input: PortfolioStateInput): CreditPortfolioState {
    if (input.isSourceLoading) return 'loading';
    if (input.idCount === 0) return 'ready';
    if (input.portfolio) return portfolioStateOf(input.portfolio.values());
    return input.error ? 'error' : 'loading';
}
